import React, { useEffect, useRef, useState, forwardRef, useImperativeHandle } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, AlertTriangle, X, Award, MountainSnow, ChevronRight } from "lucide-react";
import { fetchRecommendations } from "@/lib/api";

/**
 * Speech-bubble popup that appears attached to the Creep character.
 * - Rotates automatically every `intervalMs`.
 * - Tapping the bubble advances to the next message (or navigates to
 *   the station detail when the current message targets one).
 * - Tap X to dismiss the whole rotation.
 * - Exposes an imperative `next()` method so the Creep character's onTap
 *   can also advance messages ("hablar con el gato").
 */

const buildMessages = (rec) => {
  if (!rec) return [];
  const msgs = [];
  const season = rec.season?.key;
  const unavailable = rec.unavailable || [];

  if (rec.available_count === 0) {
    msgs.push({
      kind: "warn", icon: AlertTriangle,
      title: "Sin datos meteorológicos",
      body: "Esperando a que Meteocat vuelva a responder. Vuelve en unos minutos.",
    });
    return msgs;
  }

  if (rec.skiable_count === 0) {
    if (season === "summer") {
      msgs.push({
        kind: "season", icon: MountainSnow,
        title: "Estaciones cerradas",
        body: "Estamos en verano · las estaciones están cerradas. Vuelve en diciembre y te llevaré al Pirineo.",
      });
    } else if (season === "autumn") {
      msgs.push({
        kind: "season", icon: MountainSnow,
        title: "Aún no es temporada",
        body: "Aún no hay condiciones para esquiar. Vigilando la primera nevada.",
      });
    } else {
      msgs.push({
        kind: "warn", icon: AlertTriangle,
        title: "Hoy nada de esquí",
        body: "Ninguna estación tiene condiciones aceptables. Mejor esperar mañana.",
      });
    }
  }

  if (rec.best) {
    const b = rec.best;
    const temp = b.temperature != null ? `${Math.round(b.temperature)}°C` : null;
    const wind = b.wind_speed != null ? `viento ${Math.round(b.wind_speed)} km/h` : null;
    msgs.push({
      kind: "best", icon: Award,
      title: `Mejor hoy: ${b.station_name}`,
      body: [b.label, temp, wind].filter(Boolean).join(" · "),
      station_id: b.station_id,
    });
  }

  (rec.podium || []).slice(1, 3).forEach((p, i) => {
    msgs.push({
      kind: "best", icon: Sparkles,
      title: `${i === 0 ? "Segunda" : "Tercera"} opción: ${p.station_name}`,
      body: `${p.label} · ${p.reasons?.[0] || ""}`,
      station_id: p.station_id,
    });
  });

  (rec.warnings || []).slice(0, 2).forEach((w) => {
    msgs.push({
      kind: "warn", icon: AlertTriangle,
      title: `Evita ${w.station_name}`,
      body: w.reason,
      station_id: w.station_id,
    });
  });

  if (unavailable.length > 0) {
    msgs.push({
      kind: "season", icon: MountainSnow,
      title: `${unavailable.length} sin datos`,
      body: `${unavailable.slice(0, 3).map((u) => u.station_name).join(", ")}${unavailable.length > 3 ? "…" : ""}: reintentando en breve.`,
    });
  }

  return msgs;
};

const KIND_STYLES = {
  best:   { border: "rgba(138,107,255,0.55)", glow: "rgba(138,107,255,0.35)", accent: "#8A6BFF" },
  warn:   { border: "rgba(214,0,47,0.55)",   glow: "rgba(214,0,47,0.35)",   accent: "#FF4D6B" },
  season: { border: "rgba(255,206,0,0.55)",  glow: "rgba(255,206,0,0.25)",  accent: "#FFCE00" },
};

export const CreepPopup = forwardRef(({ intervalMs = 9000, side = "right" }, ref) => {
  const [rec, setRec] = useState(null);
  const [messages, setMessages] = useState([]);
  const [idx, setIdx] = useState(0);
  const [visible, setVisible] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const timer = useRef(null);
  const navigate = useNavigate();

  useImperativeHandle(ref, () => ({
    next: () => {
      if (messages.length === 0) return;
      setDismissed(false);
      setVisible(true);
      setIdx((i) => (i + 1) % messages.length);
    },
    show: () => { setDismissed(false); setVisible(true); },
  }), [messages.length]);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        const data = await fetchRecommendations();
        if (!mounted) return;
        setRec(data);
        const msgs = buildMessages(data);
        setMessages(msgs);
        if (msgs.length > 0) {
          setTimeout(() => mounted && setVisible(true), 1200);
        }
      } catch {
        /* silently ignore */
      }
    };
    load();
    const refresh = setInterval(load, 5 * 60 * 1000);
    return () => { mounted = false; clearInterval(refresh); };
  }, []);

  useEffect(() => {
    if (dismissed || messages.length === 0 || !visible) return;
    timer.current = setTimeout(() => {
      setIdx((i) => (i + 1) % messages.length);
    }, intervalMs);
    return () => clearTimeout(timer.current);
  }, [idx, messages, visible, dismissed, intervalMs]);

  if (dismissed || !visible || messages.length === 0) return null;

  const msg = messages[idx];
  const styles = KIND_STYLES[msg.kind] || KIND_STYLES.best;
  const Icon = msg.icon;
  const clickable = !!msg.station_id;

  // Position offset relative to Creep character. `side` chooses which side.
  const containerPos = side === "left"
    ? "right-[70%] top-[-4%] items-end"
    : "left-[70%] top-[-4%] items-start";
  const tailStyle = side === "left"
    ? { right: 18, bottom: -6, borderRight: `1px solid ${styles.border}`, borderBottom: `1px solid ${styles.border}` }
    : { left: 22, bottom: -6, borderRight: `1px solid ${styles.border}`, borderBottom: `1px solid ${styles.border}` };

  const onBubbleClick = () => {
    if (clickable) {
      navigate(`/station/${msg.station_id}`);
      return;
    }
    // advance to next message
    setIdx((i) => (i + 1) % messages.length);
  };

  return (
    <div
      className={`absolute z-30 flex flex-col ${containerPos}`}
      style={{ maxWidth: "min(320px, 78vw)" }}
      data-testid="creep-popup-wrap"
    >
      <AnimatePresence mode="wait">
        <motion.button
          key={idx}
          initial={{ opacity: 0, y: -6, scale: 0.9 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: -4, scale: 0.95 }}
          transition={{ duration: 0.32, ease: [0.22, 1, 0.36, 1] }}
          onClick={onBubbleClick}
          data-testid="creep-popup"
          className="relative text-left cursor-pointer group"
          style={{
            background: "rgba(11,11,18,0.9)",
            border: `1px solid ${styles.border}`,
            boxShadow: `0 0 32px ${styles.glow}`,
            backdropFilter: "blur(20px) saturate(160%)",
            borderRadius: 20,
            padding: "12px 34px 12px 12px",
          }}
        >
          <span
            aria-hidden
            className="absolute w-3 h-3 rotate-45"
            style={{ background: "rgba(11,11,18,0.9)", ...tailStyle }}
          />
          <span
            role="button"
            aria-label="Cerrar aviso"
            data-testid="creep-popup-close"
            onClick={(e) => { e.stopPropagation(); setDismissed(true); }}
            className="absolute top-1.5 right-1.5 w-6 h-6 rounded-full flex items-center justify-center text-white/50 hover:text-white hover:bg-white/8"
          >
            <X size={12} />
          </span>

          <div className="flex items-start gap-2.5">
            <div
              className="w-8 h-8 rounded-full flex items-center justify-center shrink-0"
              style={{ background: `${styles.accent}1F`, border: `1px solid ${styles.border}` }}
            >
              <Icon size={14} style={{ color: styles.accent }} />
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-[10px] uppercase tracking-[0.25em] flex items-center gap-1" style={{ color: styles.accent }}>
                <span>Creep te avisa</span>
                {clickable && <ChevronRight size={11} className="opacity-70 group-hover:translate-x-0.5 transition-transform" />}
              </div>
              <div className="text-sm font-semibold text-white mt-0.5 leading-tight">
                {msg.title}
              </div>
              {msg.body && (
                <div className="text-[11px] text-white/70 mt-1 leading-snug">
                  {msg.body}
                </div>
              )}
              {clickable && (
                <div className="text-[10px] text-white/45 mt-1.5">Toca para ir a la estación</div>
              )}
            </div>
          </div>

          {messages.length > 1 && (
            <div className="mt-2.5 flex items-center gap-1">
              {messages.map((_, i) => (
                <span
                  key={i}
                  className="h-1 rounded-full transition-all"
                  style={{
                    width: i === idx ? 18 : 6,
                    background: i === idx ? styles.accent : "rgba(255,255,255,0.15)",
                  }}
                />
              ))}
            </div>
          )}
        </motion.button>
      </AnimatePresence>
    </div>
  );
});

export default CreepPopup;
